/**
 * navConfig.js — sidebar navigation definitions for Aircom Connect admin shell.
 *
 * Extracted from Layout.js (Iter 44 refactor) so the layout component stays
 * focused on rendering and the menu structure is a pure data definition that
 * can be unit-tested or reordered without touching the JSX.
 *
 * Each entry has:
 *   - category: section header label
 *   - items: array of { path, icon (lucide component), label }
 */
import {
  LayoutDashboard,
  Trophy,
  Users,
  Receipt,
  Target,
  Ticket,
  DollarSign,
  Gift,
  Clock,
  Wallet,
  Bell,
  Mail,
  CreditCard,
  ShoppingBag,
  RefreshCw,
  Package,
  ArrowUpCircle,
  UserCheck,
  Settings,
  Send,
  BarChart3,
  BookOpen,
  Scale,
  Banknote,
  GitCompare,
  Camera,
  ArrowDownCircle,
  Bitcoin,
  ShieldCheck,
  X,
  Globe,
  Pause,
  Trash2,
  ArrowUpDown,
} from 'lucide-react';

export const adminNavItems = [
  {
    category: 'Principal',
    items: [
      { path: '/admin/inicio', icon: Globe, label: 'Inicio' },
      { path: '/', icon: LayoutDashboard, label: 'Panel' },
      { path: '/admin/administracion', icon: DollarSign, label: 'Administración' },
      { path: '/leaderboard', icon: Trophy, label: 'Clasificación' },
    ],
  },
  {
    category: 'Gestión',
    items: [
      { path: '/gestores', icon: Users, label: 'Gestores' },
      { path: '/admin/centro-acceso', icon: ShieldCheck, label: 'Centro de Acceso CEO' },
      { path: '/ventas', icon: Receipt, label: 'Ventas' },
      { path: '/metas', icon: Target, label: 'Metas' },
    ],
  },
  {
    category: 'Finanzas',
    items: [
      { path: '/contabilidad', icon: DollarSign, label: 'Contabilidad' },
      { path: '/admin/wallet-ceo', icon: Wallet, label: 'Wallet CEO (25%)' },
      { path: '/recompensas', icon: Gift, label: 'Recompensas' },
    ],
  },
  {
    category: 'Sistema',
    items: [
      { path: '/notificaciones', icon: Bell, label: 'Próximos Vuelos' },
      { path: '/email-settings', icon: Mail, label: 'Configurar Email' },
    ],
  },
  {
    category: 'Operaciones',
    items: [
      { path: '/workflow', icon: LayoutDashboard, label: 'Tablero de Operaciones' },
      { path: '/workflow/providers', icon: UserCheck, label: 'Proveedores' },
      { path: '/workflow/provider-payment-methods', icon: CreditCard, label: 'Métodos de Pago' },
      { path: '/workflow/queues', icon: Package, label: 'Cola Operacional' },
    ],
  },
  {
    category: 'Motor Financiero',
    items: [
      { path: '/admin/recargas', icon: ArrowDownCircle, label: 'Recargas (Depósitos)' },
      { path: '/admin/tesoreria-usdt', icon: Bitcoin, label: 'Tesorería USDT' },
      { path: '/admin/finance/ledger', icon: BookOpen, label: 'Libro Mayor' },
      { path: '/admin/finance/trial-balance', icon: Scale, label: 'Balance de Comprobación' },
      { path: '/admin/finance/settlements', icon: Banknote, label: 'Liquidaciones' },
      { path: '/admin/finance/reconciliations', icon: GitCompare, label: 'Reconciliaciones' },
      { path: '/admin/finance/snapshots', icon: Camera, label: 'Snapshots' },
      { path: '/admin/card-bins', icon: CreditCard, label: 'Registro de BINs' },
      { path: '/admin/service-pause', icon: Pause, label: 'Pausa de Servicios' },
      { path: '/admin/gestores-rendicion', icon: Wallet, label: '🧾 Rendición Gestores' },
      { path: '/admin/cleanup-test-data', icon: Trash2, label: 'Limpiar datos de prueba' },
    ],
  },
  {
    category: 'Portal Clientes',
    items: [
      { path: '/admin/analytics', icon: LayoutDashboard, label: 'Panel Clientes' },
      { path: '/admin/clientes', icon: UserCheck, label: 'Clientes' },
      { path: '/admin/ordenes-clientes', icon: Package, label: 'Órdenes' },
      { path: '/admin/divisas', icon: RefreshCw, label: 'Divisas' },
      { path: '/admin/mensajes', icon: Mail, label: 'Mensajes' },
      { path: '/admin/wallet-recargas', icon: ArrowUpCircle, label: 'Recargas (Wallet Pasajes)' },
      { path: '/admin/recharge-config', icon: Wallet, label: 'Configurar Recargas' },
      { path: '/admin/metodos-pago', icon: CreditCard, label: 'Métodos de Pago' },
      { path: '/admin/servicios', icon: ShoppingBag, label: 'Servicios' },
      { path: '/admin/tasas', icon: RefreshCw, label: 'Tasas de Cambio' },
      { path: '/admin/auditoria', icon: Clock, label: 'Auditoría' },
    ],
  },
  {
    category: 'API B2B',
    items: [
      { path: '/admin/b2b-dashboard', icon: BarChart3, label: 'Panel B2B' },
      { path: '/admin/b2b-fees', icon: Settings, label: 'Tarifas B2B' },
      { path: '/admin/b2b-orders', icon: Send, label: 'Órdenes API' },
    ],
  },
  {
    // CEO mirror of the gestor remittance panel (without Wallet & Mis Pasajeros,
    // covered by Contabilidad + admin user management). Routes reuse the same
    // components inside the admin shell.
    category: 'Remesas (vista CEO)',
    items: [
      { path: '/admin/remesas/inicio',       icon: LayoutDashboard, label: 'Inicio Remesas' },
      { path: '/admin/remesas/conversor',    icon: RefreshCw,       label: 'Conversor' },
      { path: '/admin/remesas/enviar',       icon: Send,            label: 'Enviar Remesa' },
      { path: '/admin/remesas/pendientes',   icon: Clock,           label: 'Pendientes' },
      { path: '/admin/remesas/rechazadas',   icon: X,               label: 'Rechazadas' },
      { path: '/admin/remesas/historial',    icon: Receipt,         label: 'Historial' },
      { path: '/admin/zona-de-cambios',      icon: ArrowUpDown,     label: '💱 Zona de Cambios' },
      { path: '/admin/remesas/cuentas',      icon: CreditCard,      label: 'Cuentas Bancarias' },
      { path: '/admin/remesas/promotores',   icon: Users,           label: 'Promotores' },
      { path: '/admin/remesas/trabajadores', icon: UserCheck,       label: 'Trabajadores' },
      { path: '/admin/remesas/usuarios',     icon: UserCheck,       label: 'Usuarios' },
      { path: '/admin/remesas/soporte',      icon: Mail,            label: 'Soporte' },
      { path: '/admin/remesas/faq',          icon: BookOpen,        label: 'FAQ' },
    ],
  },
];

export const gestorNavItemsConfig = (user) => [
  { path: '/mis-ventas',         icon: Ticket, label: 'Mis Pasajeros' },
  { path: `/wallet/${user?.id}`, icon: Wallet, label: 'Mi Wallet' },
  { path: '/wallet-ganancias',   icon: Gift,   label: 'Mis Ganancias' },
];
